import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { PageHeader } from '@/components/app/PageHeader';
import { EmptyState, Panel, PanelHeader, SkeletonTable } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { EditedMark, ExampleDataNote, Field, FormSheet, HistoryList, MiniStat, RefSelect, RefsErrorNote, RefsGate, SelectField, SheetSection, inputClass, useDriftRefs } from '@/components/app/DriftUI';
import { NbDateInput } from '@/components/app/DateTimeFields';
import { DriftFilterBar, useDriftFilters } from '@/components/app/DriftFilters';
import { byUnitText, createConsumption, deleteConsumption, getConsumption, isActiveRef, listConsumption, updateConsumption } from '@/lib/driftApi';
import { formatDateShort, formatDateWeekday, isValidDate } from '@/domain/time';
import { fmtNumber } from '@/domain/numbers';
import { readJson } from '@/state/storage';
import { cn } from '@/lib/utils';

/*
  Vareforbruk på prosjekt: rørdeler, masser og materialer. Enheten følger varen, og mengder summeres
  kun innen samme enhet (tonn legges aldri sammen med meter eller stk). Forbruk – også masser i tonn –
  er ikke produserte tonn og inngår ikke i produksjons-KPI-ene. Rettinger lagres som historikk.
  Kunde → prosjekt velges eksplisitt (som i timeføringen), og kunden vises i lister og summer – aldri gjemt bak prosjektnavnet.
*/

const EMPLOYEE_KEY = 'hr:drift-employee';
const FILTER_KEYS = ['projectId', 'subprojectId', 'employeeId', 'category', 'itemId'];
const MORE_KEYS = ['subprojectId', 'category', 'itemId', 'employeeId'];
const newLine = () => ({ key: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, category: '', itemId: '', quantity: '', comment: '' });
const emptyForm = (today, employeeId, projectId = '', subprojectId = '', customerId = '') => ({ date: today, customerId, projectId, subprojectId, employeeId: employeeId ?? '', comment: '', lines: [newLine()], reason: '' });
const toForm = (c) => ({
  date: c.date,
  customerId: c.project?.customerId ?? '',
  projectId: c.projectId ?? '',
  subprojectId: c.subprojectId ?? '',
  employeeId: c.employeeId ?? '',
  comment: c.comment ?? '',
  lines: c.lines.map((l, i) => ({ key: `${c.id}-${i}`, category: l.category ?? l.item?.category ?? '', itemId: l.itemId, quantity: String(l.quantity), comment: l.comment ?? '' })),
  reason: '',
});
const toBody = (form) => ({
  date: form.date,
  projectId: form.projectId,
  subprojectId: form.subprojectId || null,
  employeeId: form.employeeId || null,
  lines: form.lines.map((l) => ({ itemId: l.itemId, quantity: Number(l.quantity), comment: l.comment || null })),
  comment: form.comment || null,
});

const qtyText = (q, unit) => fmtNumber(q, unit === 'stk' ? 0 : unit === 't' ? 1 : 0);
const unitLabel = (refs, u) => refs?.units?.[u] ?? u;

function validate(f) {
  const e = {};
  if (!f.date || !isValidDate(f.date)) e.date = 'Velg dato (dd.mm.åååå)';
  if (!f.projectId) e.projectId = 'Velg kunde og prosjekt';
  const lineErrors = f.lines.map((l) => (!l.itemId ? 'Velg vare' : !(Number(l.quantity) > 0) ? 'Oppgi antall' : null));
  if (lineErrors.some(Boolean)) e.lines = lineErrors;
  return e;
}

function ConsumptionForm({ refs, form, setForm, errors, editing }) {
  const project = refs.projects.find((p) => p.id === form.projectId);
  // Kunde eksplisitt: aktive kunder + kunden på posten. Prosjektlisten begrenses til valgt kunde.
  const customers = refs.customers ?? [];
  const customerOptions = customers.filter((c) => isActiveRef(c) || c.id === form.customerId).map((c) => ({ value: c.id, label: c.name, hint: isActiveRef(c) ? undefined : 'arkivert' }));
  const projects = form.customerId ? refs.projects.filter((p) => p.customerId === form.customerId || p.id === form.projectId) : refs.projects;
  const subprojects = (project?.subprojects ?? []).filter((s) => isActiveRef(s) || s.id === form.subprojectId); // alltid begrenset til valgt prosjekt; arkiverte kun når de alt står på posten
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (key, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
  const removeLine = (key) => setForm((f) => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((l) => l.key !== key) : f.lines }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, newLine()] }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Dato" htmlFor="v-date" required error={errors.date} hint={form.date && isValidDate(form.date) ? formatDateWeekday(form.date) : 'dd.mm.åååå'}>
          <NbDateInput id="v-date" value={form.date} onChange={set('date')} testId="forbruk-date" />
        </Field>
        <Field label="Registrert av" htmlFor="v-emp">
          <RefSelect id="v-emp" value={form.employeeId} onValueChange={set('employeeId')} allowEmpty emptyLabel="Ikke oppgitt" list={refs.employees} testId="forbruk-employee" />
        </Field>
      </div>
      <Field label="Kunde" htmlFor="v-cust" required error={!form.customerId && errors.projectId ? errors.projectId : undefined}>
        <SelectField
          id="v-cust"
          value={form.customerId}
          onValueChange={(v) => setForm((f) => ({ ...f, customerId: v, ...(refs.projects.find((p) => p.id === f.projectId)?.customerId === v ? {} : { projectId: '', subprojectId: '' }) }))}
          options={customerOptions}
          placeholder="Velg kunde"
          testId="forbruk-customer"
        />
      </Field>
      <Field label="Prosjekt" htmlFor="v-proj" required error={form.customerId ? errors.projectId : undefined} hint={project?.status === 'avsluttet' ? 'Avsluttet prosjekt – beholdes kun på eksisterende registrering' : undefined}>
        <RefSelect
          id="v-proj"
          value={form.projectId}
          onValueChange={(v) => setForm((f) => ({ ...f, projectId: v, subprojectId: '', customerId: refs.projects.find((p) => p.id === v)?.customerId ?? f.customerId }))}
          list={projects}
          getHint={(p) => (form.customerId ? p.location : p.customer)}
          placeholder={form.customerId ? 'Velg prosjekt' : 'Velg kunde først, eller prosjekt direkte'}
          testId="forbruk-project"
        />
      </Field>
      {project && (
        <p className="-mt-2 text-[13px] text-[var(--hr-muted)]" data-testid="forbruk-selected-customer-project">
          Valgt: <span className="font-medium text-[var(--hr-text)]">{project.customer}</span> – {project.name}
        </p>
      )}
      {subprojects.length > 0 && (
        <Field label="Underprosjekt" htmlFor="v-sub" hint="Valgfritt – kun underprosjekter i valgt prosjekt">
          <SelectField id="v-sub" value={form.subprojectId} onValueChange={set('subprojectId')} allowEmpty emptyLabel="Ingen underprosjekt" options={subprojects.map((s) => ({ value: s.id, label: s.name }))} testId="forbruk-subproject" />
        </Field>
      )}

      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="text-[13px] font-medium text-[var(--hr-text)]">Varelinjer</div>
          <Button type="button" variant="outline" size="sm" className="h-8 rounded-[8px] bg-white text-[13px]" onClick={addLine} data-testid="forbruk-add-line">
            <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Ny linje
          </Button>
        </div>
        <div className="space-y-3">
          {form.lines.map((l, i) => {
            const items = refs.items.filter((it) => !l.category || it.category === l.category);
            const item = refs.items.find((it) => it.id === l.itemId);
            const err = errors.lines?.[i];
            return (
              <div key={l.key} className={cn('rounded-[12px] border p-3', err ? 'border-[rgba(180,35,24,0.4)]' : 'border-[var(--hr-hairline)]')} data-testid={`forbruk-line-${i}`}>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Kategori" htmlFor={`v-cat-${i}`}>
                    <SelectField id={`v-cat-${i}`} value={l.category} onValueChange={(v) => setLine(l.key, { category: v, itemId: '' })} allowEmpty emptyLabel="Alle kategorier" options={refs.categories.filter((c) => isActiveRef(c) || c.id === l.category).map((c) => ({ value: c.id, label: c.label }))} testId={`forbruk-line-category-${i}`} />
                  </Field>
                  <Field label="Vare" htmlFor={`v-item-${i}`} required error={err === 'Velg vare' ? err : undefined}>
                    <RefSelect id={`v-item-${i}`} value={l.itemId} onValueChange={(v) => setLine(l.key, { itemId: v, category: refs.items.find((it) => it.id === v)?.category ?? l.category })} list={items} getHint={(it) => unitLabel(refs, it.unit)} testId={`forbruk-line-item-${i}`} />
                  </Field>
                </div>
                <div className="mt-3 grid grid-cols-[1fr_auto] items-end gap-3">
                  <Field label="Antall" htmlFor={`v-qty-${i}`} required error={err === 'Oppgi antall' ? err : undefined}>
                    <div className="flex items-center gap-2">
                      <Input id={`v-qty-${i}`} type="number" inputMode="decimal" min={0} step={item?.unit === 'stk' ? 1 : 0.1} value={l.quantity} onChange={(e) => setLine(l.key, { quantity: e.target.value })} placeholder="0" className={cn(inputClass, 'w-32 text-right')} data-testid={`forbruk-line-qty-${i}`} />
                      <span className="min-w-[40px] text-[14px] font-medium text-[var(--hr-text)]" data-testid={`forbruk-line-unit-${i}`}>{item ? unitLabel(refs, item.unit) : <span className="text-[var(--hr-muted-2)]">enhet</span>}</span>
                    </div>
                  </Field>
                  <Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-[8px]" onClick={() => removeLine(l.key)} disabled={form.lines.length === 1} aria-label="Fjern linje" data-testid={`forbruk-line-remove-${i}`}>
                    <Trash2 className="h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" />
                  </Button>
                </div>
                <Input value={l.comment} onChange={(e) => setLine(l.key, { comment: e.target.value })} maxLength={300} placeholder="Kommentar til linjen (valgfritt)" aria-label="Kommentar til linjen" className={cn(inputClass, 'mt-3 h-10 text-[14px]')} />
              </div>
            );
          })}
        </div>
      </div>

      <Field label="Kommentar" htmlFor="v-comment">
        <Textarea id="v-comment" value={form.comment} onChange={(e) => set('comment')(e.target.value)} rows={2} maxLength={1000} placeholder="Hvor ble varene brukt?" className="min-h-[72px] rounded-[10px] border-[var(--hr-border)] text-[16px] shadow-none lg:text-[14px]" data-testid="forbruk-comment" />
      </Field>

      {editing && (
        <Field label="Årsak til endringen" htmlFor="v-reason" hint="Valgfritt. Lagres i historikken sammen med før/etter.">
          <Input id="v-reason" value={form.reason} onChange={(e) => set('reason')(e.target.value)} maxLength={300} placeholder="F.eks. «Feil mengde ført»" className={inputClass} data-testid="forbruk-reason" />
        </Field>
      )}
    </div>
  );
}

export default function VareforbrukPage() {
  const { refs, error: refsError, loading: refsLoading, retry: retryRefs } = useDriftRefs();
  const f = useDriftFilters({ keys: FILTER_KEYS });
  const { today } = f;
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [sheet, setSheet] = useState(null); // null | { mode: 'ny' } | { mode: 'rediger', entry }
  const [form, setForm] = useState(() => emptyForm(today, readJson(window.localStorage, EMPLOYEE_KEY, '')));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const handledUrl = useRef({ ny: false, rediger: null });

  useEffect(() => {
    document.title = 'Vareforbruk · Hardanger Rock';
  }, []);

  const queryKey = JSON.stringify(f.query);
  const load = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      setData(await listConsumption(JSON.parse(queryKey)));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [queryKey]);

  useEffect(() => {
    load();
  }, [load]);

  const activeProject = f.values.projectId !== 'alle' ? f.values.projectId : '';
  const activeSub = f.values.subprojectId !== 'alle' ? f.values.subprojectId : '';

  const openNew = useCallback(() => {
    setErrors({});
    const project = refs?.projects.find((p) => p.id === activeProject);
    setForm(emptyForm(today, readJson(window.localStorage, EMPLOYEE_KEY, ''), activeProject, activeSub, project?.customerId ?? ''));
    if (!refs) retryRefs();
    setSheet({ mode: 'ny' });
  }, [today, activeProject, activeSub, refs, retryRefs]);

  const openEdit = useCallback(
    (entry) => {
      setErrors({});
      setForm(toForm(entry));
      if (!refs) retryRefs();
      setSheet({ mode: 'rediger', entry });
    },
    [refs, retryRefs],
  );

  useEffect(() => {
    if (f.params.get('ny') && !handledUrl.current.ny) {
      handledUrl.current.ny = true;
      openNew();
      f.remove('ny');
    }
    const id = f.params.get('rediger');
    if (id && handledUrl.current.rediger !== id) {
      handledUrl.current.rediger = id;
      getConsumption(id)
        .then((c) => openEdit(c))
        .catch(() => {
          toast.error('Fant ikke registreringen', { description: 'Den kan være slettet.' });
          f.remove('rediger');
        });
    }
  }, [f, openNew, openEdit]);

  const closeSheet = () => {
    setSheet(null);
    if (f.params.get('rediger')) {
      handledUrl.current.rediger = null;
      f.remove('rediger');
    }
  };

  /** «Prøv igjen» henter både forbruk og referansedata (prosjekter, varer). */
  const retryAll = () => {
    load();
    if (!refs) retryRefs();
  };

  const save = async () => {
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      if (sheet?.mode === 'rediger') {
        const updated = await updateConsumption(sheet.entry.id, { ...toBody(form), reason: form.reason || null });
        const changed = (updated.history?.length ?? 0) > (sheet.entry.history?.length ?? 0);
        toast.success(changed ? 'Endringen er lagret' : 'Ingen endringer å lagre', { description: changed ? 'Før/etter er lagret i historikken.' : 'Registreringen var allerede slik.' });
      } else {
        await createConsumption(toBody(form));
        const proj = refs?.projects.find((p) => p.id === form.projectId);
        toast.success('Vareforbruk registrert', { description: `${form.lines.length} ${form.lines.length === 1 ? 'linje' : 'linjer'} · ${formatDateShort(form.date)}${proj ? ` · ${proj.customer} – ${proj.name}` : ''}` });
      }
      closeSheet();
      load();
    } catch (err) {
      toast.error('Kunne ikke lagre', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const c = toDelete;
    setToDelete(null);
    try {
      await deleteConsumption(c.id);
      toast.success('Registreringen er slettet');
      if (sheet?.mode === 'rediger' && sheet.entry.id === c.id) closeSheet();
      load();
    } catch (err) {
      toast.error('Kunne ikke slette', { description: err.message });
    }
  };

  const entries = useMemo(() => data?.entries ?? [], [data]);
  const sums = data?.sums;
  const lineRows = useMemo(() => entries.flatMap((c) => c.lines.map((l, i) => ({ ...l, entry: c, key: `${c.id}-${i}`, first: i === 0 }))), [entries]);
  const byUnit = sums ? Object.entries(sums.byUnit) : [];
  const projectName = activeProject ? refs?.projects?.find((p) => p.id === activeProject)?.name : null;
  const editing = sheet?.mode === 'rediger';

  return (
    <div className="space-y-5">
      <PageHeader title="Vareforbruk" lead="Rørdeler, masser og materialer brukt på prosjekt. Enheten følger varen.">
        <Button onClick={openNew} className="h-10 rounded-[10px] bg-[var(--hr-heading)] px-4 text-[14px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" data-testid="forbruk-new-button">
          <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Registrer forbruk
        </Button>
      </PageHeader>

      <ExampleDataNote>Forbruk – også masser i tonn – er ikke produserte tonn og inngår ikke i produksjons-KPI-ene.</ExampleDataNote>

      <Panel>
        <div className="grid grid-cols-2 divide-x divide-[var(--hr-hairline)] lg:grid-cols-4" data-testid="forbruk-stats">
          <MiniStat label="Registreringer" value={sums ? fmtNumber(entries.length, 0) : '–'} meta={sums ? `${lineRows.length} varelinjer` : ''} testId="forbruk-stat-count" />
          {['t', 'm', 'stk'].map((u) => (
            <MiniStat key={u} label={`Sum ${unitLabel(refs, u)}`} value={sums ? (sums.byUnit?.[u] !== undefined ? qtyText(sums.byUnit[u], u) : '0') : '–'} meta={u === 't' ? 'Masser – egen enhet' : u === 'm' ? 'Rør og bånd' : 'Deler'} testId={`forbruk-stat-${u}`} />
          ))}
        </div>
        {byUnit.some(([u]) => !['t', 'm', 'stk'].includes(u)) && (
          <div className="border-t border-[var(--hr-hairline)] px-5 py-2 text-[13px] text-[var(--hr-muted)]">
            Andre enheter: {byUnit.filter(([u]) => !['t', 'm', 'stk'].includes(u)).map(([u, q]) => `${qtyText(q, u)} ${unitLabel(refs, u)}`).join(' · ')}
          </div>
        )}
      </Panel>

      <Panel className="min-w-0">
        <PanelHeader title="Forbruksoversikt" description={projectName ? `Viser forbruk på ${projectName}. Én rad per varelinje.` : 'Én rad per varelinje. Periode og prosjekt først – øvrige valg under «Flere filtre».'} actions={lineRows.length > 0 ? <span className="hr-tabular text-[13px] text-[var(--hr-muted)]" data-testid="forbruk-count">{lineRows.length} varelinjer</span> : null} />
        <DriftFilterBar f={f} refs={refs} more={MORE_KEYS} testPrefix="forbruk" />
        {refsError && !refs && <div className="px-5 pt-5"><RefsErrorNote error={refsError} loading={refsLoading} onRetry={retryRefs} what="prosjekter og varer" testId="forbruk-refs-error" /></div>}
        {status === 'loading' && <div className="p-5"><SkeletonTable rows={6} /></div>}
        {status === 'error' && <div className="p-5"><Note tone="amber" data-testid="forbruk-load-error">Kunne ikke hente vareforbruk. <Button variant="link" className="h-auto px-1" onClick={retryAll} data-testid="forbruk-load-retry">Prøv igjen</Button></Note></div>}
        {(status === 'ready' || status === 'refreshing') && lineRows.length === 0 && (
          <div className="p-5">
            <EmptyState title="Ingen forbruk i utvalget" description="Endre periode eller filter, eller registrer forbruk." onReset={f.activeCount || f.period.preset !== '14' ? f.reset : undefined} action={<Button onClick={openNew} className="rounded-[10px]" data-testid="forbruk-empty-new">Registrer forbruk</Button>} />
          </div>
        )}
        {(status === 'ready' || status === 'refreshing') && lineRows.length > 0 && (
          <>
            <ul className="divide-y divide-[var(--hr-hairline)] lg:hidden" data-testid="forbruk-list-mobile">
              {entries.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => openEdit(c)} className="block w-full px-4 py-3 text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" data-testid={`forbruk-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="hr-tabular text-[13px] text-[var(--hr-muted)]">{formatDateShort(c.date)}{c.employee ? ` · ${c.employee.name}` : ''}</div>
                        <div className="mt-0.5 truncate text-[15px] font-semibold text-[var(--hr-heading)]" data-testid={`forbruk-card-customer-${c.id}`}>{c.project?.customer ?? '–'}</div>
                        <div className="truncate text-[14px] text-[var(--hr-text)]">{c.project?.name}{c.subproject ? ` · ${c.subproject.name}` : ''}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {c.edited && <EditedMark count={c.history.filter((h) => h.kind === 'endret').length} />}
                        <ChevronRight className="h-4 w-4 text-[var(--hr-muted-2)]" aria-hidden="true" />
                      </div>
                    </div>
                    <ul className="mt-2 space-y-1">
                      {c.lines.map((l, i) => (
                        <li key={i} className="flex items-center justify-between gap-3 text-[14px]">
                          <span className="min-w-0 truncate text-[var(--hr-text)]">{l.item?.name}</span>
                          <span className="hr-num shrink-0 font-semibold text-[var(--hr-heading)]">{qtyText(l.quantity, l.unit)} {unitLabel(refs, l.unit)}</span>
                        </li>
                      ))}
                    </ul>
                  </button>
                </li>
              ))}
            </ul>
            <div className="hr-scroll hidden overflow-x-auto lg:block" data-testid="forbruk-table-scroll">
              <Table data-testid="forbruk-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Dato</TableHead>
                    <TableHead className="hidden xl:table-cell">Kunde</TableHead>
                    <TableHead>Prosjekt</TableHead>
                    <TableHead className="hidden 2xl:table-cell">Kategori</TableHead>
                    <TableHead>Vare</TableHead>
                    <TableHead className="text-right">Antall</TableHead>
                    <TableHead>Enhet</TableHead>
                    <TableHead>Registrert av</TableHead>
                    <TableHead className="hidden xl:table-cell">Kommentar</TableHead>
                    <TableHead className="w-[1%]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineRows.map((l) => (
                    <TableRow key={l.key} className={cn(!l.first && 'border-t-0 [&>td]:pt-0')} data-testid={`forbruk-row-${l.key}`}>
                      <TableCell className="hr-tabular whitespace-nowrap align-top">
                        {l.first && (
                          <>
                            <button type="button" onClick={() => openEdit(l.entry)} className="rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`forbruk-open-${l.entry.id}`}>
                              {formatDateShort(l.entry.date)}
                            </button>
                            {l.entry.edited && <div className="mt-0.5"><EditedMark count={l.entry.history.filter((h) => h.kind === 'endret').length} testId={`forbruk-edited-${l.entry.id}`} /></div>}
                          </>
                        )}
                      </TableCell>
                      <TableCell className="hidden max-w-[200px] truncate align-top xl:table-cell" title={l.first ? l.entry.project?.customer : undefined} data-testid={l.first ? `forbruk-customer-${l.entry.id}` : undefined}>{l.first ? l.entry.project?.customer ?? <span className="text-[var(--hr-muted-2)]">–</span> : ''}</TableCell>
                      <TableCell className="align-top">
                        {l.first && (
                          <>
                            <button type="button" onClick={() => f.set('projectId', l.entry.projectId)} className="rounded-sm text-left font-medium text-[var(--hr-heading)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" title="Filtrer på prosjektet" data-testid={`forbruk-project-link-${l.entry.id}`}>
                              {l.entry.project?.name}
                            </button>
                            {l.entry.subproject && <div className="text-[13px] text-[var(--hr-muted)]">{l.entry.subproject.name}</div>}
                            {l.entry.project?.customer && <div className="truncate text-[13px] text-[var(--hr-muted)] xl:hidden" title={l.entry.project.customer}>{l.entry.project.customer}</div>}
                          </>
                        )}
                      </TableCell>
                      <TableCell className="hidden align-top 2xl:table-cell">{refs?.categories.find((c) => c.id === l.category)?.label ?? l.category}</TableCell>
                      <TableCell className="align-top font-medium text-[var(--hr-text)]">{l.item?.name}</TableCell>
                      <TableCell className="hr-tabular text-right align-top font-semibold text-[var(--hr-heading)]">{qtyText(l.quantity, l.unit)}</TableCell>
                      <TableCell className="align-top">{unitLabel(refs, l.unit)}</TableCell>
                      <TableCell className="align-top">{l.first ? l.entry.employee?.name ?? <span className="text-[var(--hr-muted-2)]">–</span> : ''}</TableCell>
                      <TableCell className="hidden max-w-[240px] truncate align-top text-[var(--hr-muted)] xl:table-cell">{l.comment ?? (l.first ? l.entry.comment : '') ?? ''}</TableCell>
                      <TableCell className="align-top">
                        {l.first && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => openEdit(l.entry)} aria-label="Åpne og rediger registrering" title="Åpne og rediger" data-testid={`forbruk-edit-${l.entry.id}`}>
                            <Pencil className="h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Panel>

      {sums?.byProject?.length > 0 && (
        <Panel>
          <PanelHeader title="Summer per kunde og prosjekt" description="Per vare, med enhet. Tonn, meter og stk summeres hver for seg." />
          <div className="grid gap-px bg-[var(--hr-hairline)] md:grid-cols-2 xl:grid-cols-3" data-testid="forbruk-project-sums">
            {sums.byProject.map((p) => (
              <div key={p.projectId} className="bg-white px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {p.customer && <div className="truncate text-[13px] text-[var(--hr-muted)]" data-testid={`forbruk-project-customer-${p.projectId}`}>{p.customer}</div>}
                    <Link to={`/prosjekter/${p.projectId}${f.carry({}, { only: [] })}`} className="block truncate text-left text-[14px] font-semibold text-[var(--hr-heading)] hover:underline" data-testid={`forbruk-project-${p.projectId}`}>
                      {p.name}
                    </Link>
                  </div>
                  <span className="hr-tabular shrink-0 text-right text-[13px] text-[var(--hr-muted)]">{byUnitText(p.byUnit, refs?.units)}</span>
                </div>
                <ul className="mt-2 divide-y divide-[var(--hr-hairline)]">
                  {p.items.map((it) => (
                    <li key={it.itemId} className="flex items-center justify-between gap-3 py-1.5 text-[14px]">
                      <span className="min-w-0 truncate text-[var(--hr-text)]">{it.name}</span>
                      <span className="hr-num shrink-0 font-medium text-[var(--hr-heading)]">{qtyText(it.quantity, it.unit)} {unitLabel(refs, it.unit)}</span>
                    </li>
                  ))}
                </ul>
                <button type="button" onClick={() => f.set('projectId', p.projectId)} className="mt-2 text-[13px] text-[var(--hr-muted)] hover:text-[var(--hr-heading)] hover:underline" data-testid={`forbruk-project-filter-${p.projectId}`}>
                  Vis linjene
                </button>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <FormSheet
        open={!!sheet}
        onOpenChange={(o) => !o && closeSheet()}
        title={editing ? 'Rediger vareforbruk' : 'Registrer vareforbruk'}
        description={editing ? 'Endringer lagres med før/etter i historikken.' : 'Flere linjer per registrering. Enheten følger varen.'}
        testId="forbruk-form-sheet"
        footer={
          <div className="flex items-center gap-2">
            {editing && (
              <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0 rounded-[10px] text-[var(--hr-muted)] hover:text-[var(--hr-red)] lg:h-10 lg:w-10" onClick={() => setToDelete(sheet.entry)} aria-label="Slett registrering" data-testid="forbruk-delete">
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
            <Button variant="outline" className="h-11 flex-1 rounded-[10px] lg:h-10" onClick={closeSheet} disabled={saving} data-testid="forbruk-cancel">
              Avbryt
            </Button>
            <Button className="h-11 flex-1 rounded-[10px] bg-[var(--hr-heading)] text-white hover:bg-[var(--hr-navy)] lg:h-10" onClick={save} disabled={saving || !refs} data-testid="forbruk-save">
              {saving ? 'Lagrer …' : editing ? 'Lagre endringer' : 'Lagre'}
            </Button>
          </div>
        }
      >
        <RefsGate refs={refs} error={refsError} loading={refsLoading} onRetry={retryRefs} what="prosjekter og varer" loadingText="Henter prosjekter og vareliste …">
          {refs && <ConsumptionForm refs={refs} form={form} setForm={setForm} errors={errors} editing={editing} />}
        </RefsGate>
        {editing && (
          <SheetSection title="Historikk" description="Hver endring lagres med før/etter, tidspunkt og «Kontor (eksempeldata)». Ikke en autentisert revisjonslogg." className="mt-6">
            <HistoryList history={sheet.entry.history} testId="forbruk-history" />
          </SheetSection>
        )}
      </FormSheet>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette registreringen?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete ? `${formatDateShort(toDelete.date)} · ${toDelete.project?.customer ? `${toDelete.project.customer} – ` : ''}${toDelete.project?.name} · ${toDelete.lines.length} ${toDelete.lines.length === 1 ? 'linje' : 'linjer'}. Sletting kan ikke angres – vurder å rette registreringen i stedet.` : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[var(--hr-red)] text-white hover:bg-[var(--hr-red)]/90" data-testid="forbruk-delete-confirm">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
